import api from './api';
import axios, { isAxiosError } from 'axios';
import { Product } from '../types';

export interface ProductsResponse {
  products: Product[];
  total: number;
  totalPages: number;
  currentPage: number;
}

// Función para normalizar texto (eliminar acentos y caracteres especiales)
const normalizeText = (text: string): string => {
  return text.toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "") // Eliminar acentos
    .replace(/[^a-z0-9\s]/g, " ") // Reemplazar caracteres especiales con espacios
    .replace(/\s+/g, " ") // Reemplazar múltiples espacios con uno solo
    .trim();
};

// Función para calcular relevancia de coincidencia
const calculateRelevance = (product: Product, searchTerm: string): number => {
  const term = normalizeText(searchTerm);
  const name = normalizeText(product.name);
  const description = normalizeText(product.description || '');
  const brand = normalizeText(product.brand || '');
  const model = normalizeText(product.model || '');
  
  let score = 0;
  
  // Coincidencia exacta en el nombre (máxima prioridad)
  if (name === term) score += 100;
  
  // Comienza con el término
  if (name.startsWith(term)) score += 80;
  
  // Contiene el término como palabra completa en el nombre
  if (new RegExp(`\\b${term}\\b`).test(name)) score += 60;
  
  // Contiene el término en el nombre
  if (name.includes(term)) score += 40;
  
  // Contiene en la marca
  if (brand.includes(term)) score += 30;
  
  // Contiene en el modelo
  if (model.includes(term)) score += 25;
  
  // Contiene en la descripción
  if (description.includes(term)) score += 20;
  
  // Búsqueda por palabras individuales
  const termWords = term.split(' ').filter(word => word.length > 2);
  termWords.forEach(word => {
    if (name.includes(word)) score += 15;
    if (brand.includes(word)) score += 10;
    if (description.includes(word)) score += 5;
  });
  
  return score;
};

export const productService = {
  getProducts: async (page: number = 1, limit: number = 10): Promise<ProductsResponse> => {
    const response = await api.get(`/products?page=${page}&limit=${limit}`);
    return {
      products: response.data.products || [],
      total: response.data.total || 0,
      totalPages: response.data.totalPages || 1,
      currentPage: response.data.currentPage || 1
    };
  },

  getAllProducts: async (): Promise<Product[]> => {
    const response = await api.get('/products?limit=10000'); // Para obtener todos los productos
    return response.data.products || [];
  },

  getProductsWithFilters: async (filters: {
    category?: string;
    family?: string;
    subfamily?: string;
    search?: string;
    page?: number;
    limit?: number;
  } = {}): Promise<ProductsResponse> => {
    const params = new URLSearchParams();
    
    if (filters.category) params.append('category', filters.category);
    if (filters.family) params.append('family', filters.family);
    if (filters.subfamily) params.append('subfamily', filters.subfamily);
    if (filters.search) params.append('search', filters.search);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    
    const response = await api.get(`/products?${params.toString()}`);
    return {
      products: response.data.products || [],
      total: response.data.total || 0,
      totalPages: response.data.totalPages || 1,
      currentPage: response.data.currentPage || 1
    };
  },

  getProduct: async (id: string): Promise<Product> => {
    const response = await api.get(`/products/${id}`);
    return response.data;
  },

  // Búsqueda original del backend (mantenemos por compatibilidad)
  searchProducts: async (query: string): Promise<Product[]> => {
    const response = await api.get(`/products/search?q=${encodeURIComponent(query)}`);
    return response.data.products || [];
  },

  // Nueva búsqueda local mejorada
  searchProductsLocal: async (query: string, categoryFilter?: string): Promise<Product[]> => {
    try {
      // Obtener todos los productos
      const allProducts = await productService.getAllProducts();
      
      if (!query.trim()) {
        return [];
      }
      
      // Filtrar productos que coincidan con la búsqueda
      let matchingProducts = allProducts.filter(product => {
        const relevance = calculateRelevance(product, query);
        return relevance > 0;
      });
      
      // Si hay un filtro de categoría, aplicarlo
      if (categoryFilter && categoryFilter !== 'all') {
        matchingProducts = matchingProducts.filter(product => 
          product.category_name === categoryFilter
        );
      }
      
      // Ordenar por relevancia
      const sortedProducts = matchingProducts
        .map(product => ({
          ...product,
          relevance: calculateRelevance(product, query)
        }))
        .sort((a, b) => b.relevance - a.relevance)
        .map(({ relevance, ...product }) => product); // Remover el campo relevance
      
      return sortedProducts;
    } catch (error) {
      console.error('Error en búsqueda local:', error);
      // Fallback a búsqueda del backend
      return await productService.searchProducts(query);
    }
  },

  getProductsByCategory: async (categoryId: string, page: number = 1, limit: number = 50): Promise<ProductsResponse> => {
    const response = await api.get(`/products/category/${categoryId}?page=${page}&limit=${limit}`);
    return {
      products: response.data.products || [],
      total: response.data.total || 0,
      totalPages: response.data.totalPages || 1,
      currentPage: response.data.currentPage || 1
    };
  },
  
  createProduct: async (productData: FormData): Promise<Product> => {
    const response = await api.post('/products', productData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data.product;
  },

  updateProduct: async (id: string, productData: FormData): Promise<Product> => {
    try {
      const response = await api.put(`/products/${id}`, productData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data.product;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Error detallado:', error.response?.data);
        const errorMessage = error.response?.data?.message || error.message;
        throw new Error(`Error al actualizar producto: ${errorMessage}`);
      }
      throw new Error('Error desconocido al actualizar el producto');
    }
  },

  deleteProduct: async (id: string): Promise<void> => {
    await api.delete(`/products/${id}`);
  },

  toggleForcePublish: async (id: string, forcePublish?: boolean): Promise<{ product: Product; message: string }> => {
    const response = await api.patch(`/products/${id}/force-publish`, { force_publish: forcePublish });
    return response.data;
  }
};